import React, { useRef, useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { Upload, File, X, Download } from 'lucide-react';
import { Button } from './ui/button';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export interface SingleFileDropZoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  existingFileName?: string | null;
  onDownloadExisting?: () => void;
  accept?: string;
  disabled?: boolean;
}

export const SingleFileDropZone: React.FC<SingleFileDropZoneProps> = ({
  file,
  onFileChange,
  existingFileName,
  onDownloadExisting,
  accept = '*/*',
  disabled = false,
}) => {
  const { t } = useLocale();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFile = (fileList: FileList | null): void => {
    if (!fileList || fileList.length === 0) return;
    onFileChange(fileList[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    addFile(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled) addFile(e.dataTransfer.files);
  };

  const handleZoneClick = (): void => {
    if (!disabled) fileInputRef.current?.click();
  };

  const showExisting = existingFileName && !file;

  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 sm:p-8 flex flex-row sm:flex-col items-center gap-4 sm:gap-0 text-left sm:text-center transition-all ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${isDragging ? 'border-primary bg-muted' : 'border-muted-foreground/25'}`}
        onClick={handleZoneClick}
      >
        <Upload className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 sm:mx-auto sm:mb-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium mb-0.5 sm:mb-2">
            {t('Drag and drop files here')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('or click to select files')}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {(file || showExisting) && (
        <div className="mt-4">
          <div className="flex items-center gap-3 p-3 border rounded-md bg-background">
            <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${showExisting && onDownloadExisting ? 'cursor-pointer underline underline-offset-2' : ''}`}
                onClick={showExisting && onDownloadExisting ? (e) => { e.stopPropagation(); onDownloadExisting(); } : undefined}
                onKeyDown={
                  showExisting && onDownloadExisting
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onDownloadExisting();
                        }
                      }
                    : undefined
                }
                role={showExisting && onDownloadExisting ? 'button' : undefined}
                tabIndex={showExisting && onDownloadExisting ? 0 : undefined}
              >
                {file ? file.name : existingFileName ?? ''}
              </p>
              {file && (
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)}
                </p>
              )}
            </div>
            {showExisting && onDownloadExisting && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadExisting();
                }}
                aria-label={t('download') || 'Download'}
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onFileChange(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                aria-label={t('remove') || 'Remove'}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
